import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { getValidToken } from "../../../utils/authUtils";
import { useReportFilter } from "../context/ReportFilterContext";
import ExpenseSummary from "./ExpenseSummary";
import DetailedExpenses from "./DetailedExpenses";
import { Loading } from "../../../components/Loading";
import axios from "axios";

const ExpenseReport: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { filterState } = useReportFilter();
  const { timeFilter, customStartDate, customEndDate, selectedVehicleIds } =
    filterState;

  const {
    data: expenseReportData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "expenseReport",
      timeFilter,
      customStartDate,
      customEndDate,
      selectedVehicleIds,
      currentPage,
      pageSize,
    ],
    queryFn: async () => {
      const token = await getValidToken();
      let url = `${process.env.REACT_APP_API_HOST}reports/overall-expense/${timeFilter}`;

      if (timeFilter === "custom") {
        url = `${process.env.REACT_APP_API_HOST}reports/overall-expense/custom`;
      }

      const params: any = {};
      if (timeFilter === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      if (selectedVehicleIds.length > 0) {
        params.vehicleIds = selectedVehicleIds;
      }

      // Add pagination parameters
      params.page = currentPage;
      params.limit = pageSize;

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: true,
  });

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [timeFilter, customStartDate, customEndDate, selectedVehicleIds]);

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  return (
    <>
      {isLoading && (
        <Loading fullScreen={false} message="Loading expense report..." />
      )}
      {isFetching && !isLoading && (
        <Loading fullScreen={false} message="Refreshing expense report..." />
      )}
      {!expenseReportData?.data?.data ||
      expenseReportData.data.data.summary.total_expenses === "0" ? (
        <Typography
          textAlign="center"
          display="flex"
          justifyContent="center"
          alignItems="center"
          variant="caption"
          sx={{
            fontSize: "0.75rem",
            color: "text.secondary",
            px: 1.5,
            pb: 1,
            backgroundColor: "white",
            width: "100%",
            height: "100%",
            minHeight: "100px",
          }}
        >
          No expense data available
        </Typography>
      ) : (
        <>
          <ExpenseSummary data={expenseReportData.data.data} />
          <DetailedExpenses
            data={expenseReportData.data.data}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}
    </>
  );
};

export default ExpenseReport;
